import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faViber } from '@fortawesome/free-brands-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import AddEditAppointmentModal from './AddEditAppointmentModal';
import AddEditReservationModal from './AddEditReservationModal';
import { useSalonCalendarState } from './SalonCalendarState';
import DatePickerButton from './DatePickerButton';
import { buildReservationNotesDisplay } from '../../utils/reservationNotes';
import './Salon.css';

const ManageAppointments = ({ salonId, userRole, onNavigateToReservations, currentPage: urlPage = 1, onPageChange }) => {
  const { t, getMonthName, formatWeddingDate, getDayName } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    currentDate,
    viewMode,
    applyDefaultViewFromSettings,
    getWeekDates,
    getMonthDates,
    navigateWeek,
    navigateMonth,
    navigateDay,
    goToToday,
    goToDate,
    handleSwitchToDayView,
    handleSwitchToWeekView,
    handleSwitchToMonthView,
    getFetchDateRange,
  } = useSalonCalendarState('week');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [reservationPrefillData, setReservationPrefillData] = useState(null);

  const fetchCalendarSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const isMobile = window.innerWidth <= 768;
      
      const settingKey = isMobile ? 'calendar_view_mobile' : 'calendar_view_desktop';
      
      try {
        const response = await axios.get(`/api/salons/${salonId}/settings/${settingKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.value) {
          applyDefaultViewFromSettings(response.data.value);
        }
      } catch (error) {
        console.log('Calendar view setting not found, using default');
      }
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
    }
  }, [salonId, applyDefaultViewFromSettings]);

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const { startDate, endDate } = getFetchDateRange();
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const start = formatDate(startDate);
      const end = formatDate(endDate);
      
      const response = await axios.get(`/api/salons/${salonId}/appointments?start_date=${start}&end_date=${end}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAppointments(response.data.appointments || []);
      setError('');
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setError('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [salonId, getFetchDateRange]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  // Listen for window resize to update calendar view based on screen size
  useEffect(() => {
    const handleResize = () => {
      fetchCalendarSettings();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchCalendarSettings]);

  const getAppointmentsForDate = (date) => {
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.appointment_datetime);
      return appointmentDate.toDateString() === date.toDateString();
    });
  };

  const formatTime = (datetimeFormatted) => {
    if (!datetimeFormatted) return '';
    // Extract time from formatted datetime (YYYY-MM-DDTHH:MM)
    const timePart = datetimeFormatted.split('T')[1];
    return timePart || '';
  };

  const handleAddAppointmentClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAppointmentAdded = () => {
    fetchAppointments(); // Refresh the appointments list
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleOpenReservationModal = (prefillData) => {
    console.log('Opening reservation modal with data:', prefillData);
    setReservationPrefillData(prefillData);
    setIsReservationModalOpen(true);
  };

  const handleReservationAdded = () => {
    // Close the reservation modal
    setIsReservationModalOpen(false);
    setReservationPrefillData(null);
    
    // Refresh appointments to show updated data
    fetchAppointments();
  };

  const openReservationInEditMode = async (reservationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/reservations/${reservationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setReservationPrefillData({
        ...response.data.reservation,
        mode: 'edit'
      });
      setIsReservationModalOpen(true);
    } catch (error) {
      console.error('Error fetching reservation data:', error);
    }
  };

  const handleAppointmentClick = async (appointment) => {
    if (appointment.type && appointment.type !== 'appointment') {
      // For reservation appointments (measurement, pickup, return), fetch full reservation data
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}/reservations/${appointment.reservation_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Set the full reservation data for edit mode
        setReservationPrefillData({
          ...response.data.reservation,
          mode: 'edit'
        });
        setIsReservationModalOpen(true);
      } catch (error) {
        console.error('Error fetching reservation data:', error);
        // Fallback to add mode if fetch fails
        setReservationPrefillData({
          reservation_id: appointment.reservation_id,
          dress_name: appointment.dress_name,
          mode: 'add'
        });
        setIsReservationModalOpen(true);
      }
    } else {
      // For regular appointments, open appointment modal
      setSelectedAppointment(appointment);
      setIsEditModalOpen(true);
    }
  };

  const handleOpenLinkedReservation = (reservationId) => {
    if (!reservationId) return;
    setSelectedAppointment(null);
    setIsEditModalOpen(false);
    openReservationInEditMode(reservationId);
  };

  const renderDayView = () => {
    const dayAppointments = appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.appointment_datetime);
      return appointmentDate.toDateString() === currentDate.toDateString();
    });

    // Sort appointments by time
    dayAppointments.sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));

    return (
      <div className="calendar-day-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateDay(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateDay(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-day-content">
          {dayAppointments.length === 0 ? (
            <div className="no-appointments">
              <p>{t('appointments.noAppointmentsForDay')}</p>
            </div>
          ) : (
            <div className="day-appointments-list">
              {dayAppointments.map((appointment, index) => (
                 <div 
                   key={index} 
                   className={`appointment-item ${appointment.type || 'appointment'}`}
                   onClick={() => handleAppointmentClick(appointment)}
                   style={{ cursor: 'pointer' }}
                 >
                   <div className="appointment-main-content">
                     <div className="appointment-time">
                       {formatTime(appointment.appointment_datetime_formatted)}
                     </div>
                     <div className="appointment-type">
                       {appointment.type === 'measurement' && `📏 ${t('appointments.measurement')}`}
                       {appointment.type === 'pickup' && `📦 ${t('appointments.pickup')}`}
                       {appointment.type === 'return' && `↩️ ${t('appointments.return')}`}
                       {appointment.type === 'appointment' && `👤 ${t('appointments.title')}`}
                     </div>
                     <div className="appointment-client">
                       {appointment.type && appointment.type !== 'appointment' ? (
                         <>
                           <div className="appointment-bride-name">
                             {appointment.name} {appointment.surname}
                             {appointment.bride_city ? ` - ${appointment.bride_city}` : ''}
                           </div>
                           <div className="appointment-dress-name">
                             {appointment.dress_name || 'Unknown Dress'}
                           </div>
                         </>
                       ) : (
                         `${appointment.name} ${appointment.surname}`
                       )}
                     </div>
                    <div className="appointment-wedding-date">
                      {t('appointments.weddingDate')}: {appointment.wedding_date ? formatWeddingDate(appointment.wedding_date) : '-'}
                    </div>
                     {appointment.notes && (!appointment.type || appointment.type === 'appointment') && (
                       <div className="appointment-notes">
                         {appointment.notes}
                       </div>
                     )}
                    {appointment.type && appointment.type !== 'appointment' && (() => {
                      const reservationNote = buildReservationNotesDisplay(appointment, t);
                      return reservationNote ? (
                        <div className="appointment-notes">
                          {reservationNote}
                        </div>
                      ) : null;
                    })()}
                   </div>
                   <div className="appointment-contact-buttons">
                     {appointment.phone_number && (
                       <button
                         className="contact-btn phone-btn"
                         onClick={(e) => {
                           e.stopPropagation();
                           window.open(`tel:${appointment.phone_number}`, '_self');
                         }}
                         title={`${t('appointments.call')} ${appointment.phone_number}`}
                       >
                         📞
                       </button>
                     )}
                     {appointment.phone_number && (
                       <button
                         className="contact-btn viber-btn"
                         onClick={(e) => {
                           e.stopPropagation();
                           const viberUrl = `viber://chat?number=${appointment.phone_number}`;
                           window.open(viberUrl, '_self');
                         }}
                         title={t('appointments.openViberChat')}
                       >
                         <FontAwesomeIcon icon={faViber} />
                       </button>
                     )}
                     {appointment.instagram_handle && (
                       <button
                         className="contact-btn instagram-btn"
                         onClick={(e) => {
                           e.stopPropagation();
                           const instagramUrl = appointment.instagram_handle.startsWith('@') 
                             ? `https://instagram.com/${appointment.instagram_handle.substring(1)}`
                             : `https://instagram.com/${appointment.instagram_handle}`;
                           window.open(instagramUrl, '_blank');
                         }}
                         title={t('appointments.openInstagram')}
                       >
                         <FontAwesomeIcon icon={faInstagram} />
                       </button>
                     )}
                   </div>
                 </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    
    return (
      <div className="calendar-week-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateWeek(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateWeek(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-grid">
          <div className="calendar-days-header">
            {weekDates.map((date, index) => (
              <div key={index} className="calendar-day-header">
                <div className="day-name">{getDayName(date.getDay(), true)}</div>
                <div className="day-number">{date.getDate()}</div>
              </div>
            ))}
          </div>
          
          <div className="calendar-days-content">
            {weekDates.map((date, index) => {
              const dayAppointments = getAppointmentsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              
              return (
                <div key={index} className={`calendar-day ${isToday ? 'today' : ''}`}>
                  <div className="day-reservations">
                    {dayAppointments.map((appointment, appIndex) => (
                      <div 
                        key={appIndex} 
                        className={`appointment-item ${appointment.type || 'appointment'}`}
                        onClick={() => handleAppointmentClick(appointment)}
                        style={{ cursor: 'pointer' }}
                      >
                        {(!appointment.type || appointment.type === 'appointment') && appointment.reservation_id && (
                          <FontAwesomeIcon
                            icon={faStar}
                            className="appointment-linked-star"
                            title={t('appointments.linkedReservation')}
                          />
                        )}
                        <div className="appointment-time">
                          {formatTime(appointment.appointment_datetime_formatted)}
                        </div>
                        <div className="appointment-type">
                          {appointment.type === 'measurement' && (
                            <>
                              <span className="desktop-only">📏 {t('appointments.measurement')}</span>
                              <span className="mobile-only">📏</span>
                            </>
                          )}
                          {appointment.type === 'pickup' && (
                            <>
                              <span className="desktop-only">📦 {t('appointments.pickup')}</span>
                              <span className="mobile-only">📦</span>
                            </>
                          )}
                          {appointment.type === 'return' && (
                            <>
                              <span className="desktop-only">↩️ {t('appointments.return')}</span>
                              <span className="mobile-only">↩️</span>
                            </>
                          )}
                          {appointment.type === 'appointment' && (
                            <>
                              <span className="desktop-only">👤 {t('appointments.title')}</span>
                              <span className="mobile-only">👤</span>
                            </>
                          )}
                        </div>
                        <div className="appointment-client">
                          {`${appointment.name} ${appointment.surname}`}
                        </div>
                        {appointment.type && appointment.type !== 'appointment' && (
                          <div className="appointment-dress">
                            {appointment.dress_name || 'Unknown Dress'}
                          </div>
                        )}
                        {appointment.type && appointment.type === 'appointment' && (
                          <div className="appointment-wedding-date">
                            <span className="desktop-only">{t('appointments.weddingDate')}: {appointment.wedding_date ? formatWeddingDate(appointment.wedding_date) : '-'}</span>
                            <span className="mobile-only">
                              {appointment.wedding_date ? formatWeddingDate(appointment.wedding_date, true) : '-'}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(currentDate);

    // Hide the 6th row only if it contains no days from the current month
    let datesToRender = monthDates;
    if (monthDates.length === 42) {
      const lastRow = monthDates.slice(35, 42);
      const hasCurrentMonthDay = lastRow.some(
        (d) => d.getMonth() === currentDate.getMonth()
      );
      if (!hasCurrentMonthDay) {
        datesToRender = monthDates.slice(0, 35);
      }
    }

    return (
      <div className="calendar-month-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateMonth(-1)} className="calendar-nav-btn">
              ← {t('common.previous')}
            </button>
            <button onClick={goToToday} className="calendar-today-btn">
              {t('appointments.today')}
            </button>
            <button onClick={() => navigateMonth(1)} className="calendar-nav-btn">
              {t('common.next')} →
            </button>
          </div>
          <DatePickerButton
            currentDate={currentDate}
            viewMode={viewMode}
            onDateChange={goToDate}
            getMonthName={getMonthName}
            getDayName={getDayName}
            className="calendar-title"
          />
        </div>
        
        <div className="calendar-grid month-grid">
          <div className="calendar-days-header">
            {[1, 2, 3, 4, 5, 6, 0].map(dayIndex => (
              <div key={dayIndex} className="calendar-day-header">{getDayName(dayIndex, true)}</div>
            ))}
          </div>
          
          <div className="calendar-days-content month-content">
            {datesToRender.map((date, index) => {
              const dayAppointments = getAppointmentsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isCurrentMonth = date.getMonth() === currentDate.getMonth();
              
              return (
                <div key={index} className={`calendar-day month-day ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}>
                  <div className="day-number">{date.getDate()}</div>
                  <div className="day-reservations">
                    {dayAppointments.slice(0, 3).map((appointment, appIndex) => (
                      <div 
                        key={appIndex} 
                        className={`appointment-item ${appointment.type || 'appointment'}`}
                        onClick={() => handleAppointmentClick(appointment)}
                        style={{ cursor: 'pointer' }}
                      >
                        {(!appointment.type || appointment.type === 'appointment') && appointment.reservation_id && (
                          <FontAwesomeIcon
                            icon={faStar}
                            className="appointment-linked-star"
                            title={t('appointments.linkedReservation')}
                          />
                        )}
                        <div className="appointment-time">
                          {formatTime(appointment.appointment_datetime_formatted)}
                        </div>
                        <div className="appointment-type">
                          {appointment.type === 'measurement' && '📏'}
                          {appointment.type === 'pickup' && '📦'}
                          {appointment.type === 'return' && '↩️'}
                          {appointment.type === 'appointment' && '👤'}
                        </div>
                        <div className="appointment-client">
                          {appointment.type && appointment.type !== 'appointment' 
                            ? appointment.dress_name || 'Unknown Dress'
                            : `${appointment.name} ${appointment.surname}`
                          }
                        </div>
                      </div>
                    ))}
                    {dayAppointments.length > 3 && (
                      <div className="more-reservations">
                        +{dayAppointments.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="manage-page">
        <div className="loading-message">
          <div className="loading-spinner"></div>
          {t('messages.loading.appointments')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-page">
        <div className="error-message">
          {error}
          <button onClick={fetchAppointments} className="salon-button secondary">
            {t('common.search')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-page">
      <div className="table-header">
        <div className="workspace-title">
          <h3>{t('navigation.appointments')}</h3>
          <div className="view-controls">
            <button 
              className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={handleSwitchToDayView}
            >
              {t('appointments.viewDay')}
            </button>
            <button 
              className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
              onClick={handleSwitchToWeekView}
            >
              {t('appointments.viewWeek')}
            </button>
            <button 
              className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
              onClick={handleSwitchToMonthView}
            >
              {t('appointments.viewMonth')}
            </button>
          </div>
        </div>
        <button 
          className="salon-button primary"
          onClick={handleAddAppointmentClick}
        >
          {t('appointments.addAppointment')}
        </button>
      </div>
      
      <div className="calendar-container">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>

      {/* Add Appointment Modal */}
      <AddEditAppointmentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        salonId={salonId}
        onAppointmentAdded={handleAppointmentAdded}
        mode="add"
        userRole={userRole}
        onNavigateToReservations={onNavigateToReservations}
        onOpenReservationModal={handleOpenReservationModal}
        onOpenLinkedReservation={handleOpenLinkedReservation}
      />

      {/* Edit Appointment Modal */}
      <AddEditAppointmentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedAppointment(null);
        }}
        salonId={salonId}
        onAppointmentAdded={handleAppointmentAdded}
        appointment={selectedAppointment}
        mode="edit"
        userRole={userRole}
        onNavigateToReservations={onNavigateToReservations}
        onOpenReservationModal={handleOpenReservationModal}
        onOpenLinkedReservation={handleOpenLinkedReservation}
      />

      {/* Add/Edit Reservation Modal */}
      <AddEditReservationModal
        isOpen={isReservationModalOpen}
        onClose={() => {
          setIsReservationModalOpen(false);
          setReservationPrefillData(null);
        }}
        salonId={salonId}
        onReservationAdded={handleReservationAdded}
        mode={reservationPrefillData?.mode || "add"}
        reservation={reservationPrefillData?.mode === 'edit' ? reservationPrefillData : null}
        prefillData={reservationPrefillData?.mode === 'add' ? reservationPrefillData : null}
        userRole={userRole}
      />
    </div>
  );
};

export default ManageAppointments;
